import { useMemo, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { format, parseISO } from "date-fns";
import { MoreHorizontal, Plus, RefreshCw, Search, Trash2 } from "lucide-react";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorBoundary } from "@/components/shared/error-boundary";
import { PageHeader } from "@/components/shared/page-header";
import { StatusDot } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input, NativeSelect } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useDeleteProcessPlan, useProcessPlans } from "@/features/planning/api";
import { PLAN_STATUSES, labelFor, statusTone } from "@/features/planning/constants";
import type { ProcessPlanListRow, ProcessPlanStatus } from "@/features/planning/types";
import { isSupabaseConfigured } from "@/lib/supabase";

export function PlanningPage() {
  return (
    <ErrorBoundary title="Planning crashed">
      <PlanningInner />
    </ErrorBoundary>
  );
}

function PlanningInner() {
  const navigate = useNavigate();
  const { data = [], isLoading, error, refetch, isFetching } = useProcessPlans();
  const remove = useDeleteProcessPlan();

  const [statusFilter, setStatusFilter] = useState<"all" | ProcessPlanStatus>("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [q, setQ] = useState("");
  const [deleting, setDeleting] = useState<ProcessPlanListRow | null>(null);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return data.filter((row) => {
      if (statusFilter !== "all" && row.status !== statusFilter) return false;
      if (from && row.plan_date < from) return false;
      if (to && row.plan_date > to) return false;
      if (!query) return true;
      return [row.plan_no, row.so_number, row.client_name, row.process_names]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(query);
    });
  }, [data, statusFilter, from, to, q]);

  const kpis = useMemo(
    () => ({
      draft: data.filter((r) => r.status === "draft").length,
      planned: data.filter((r) => r.status === "planned").length,
      open: data.filter((r) => ["planned", "in_progress"].includes(r.status)).length,
    }),
    [data],
  );

  if (!isSupabaseConfigured) {
    return (
      <EmptyState
        title="Supabase not configured"
        description="Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY to use planning."
      />
    );
  }

  return (
    <div className="space-y-3">
      <PageHeader
        title="Production planning"
        description="Process plans · stages · machines · planned I/O"
        actions={
          <>
            <Button type="button" variant="outline" size="sm" disabled={isFetching} onClick={() => refetch()}>
              <RefreshCw className={isFetching ? "animate-spin" : ""} />
              Refresh
            </Button>
            <Button type="button" size="sm" onClick={() => navigate({ to: "/planning/new" })}>
              <Plus />
              New plan
            </Button>
          </>
        }
      />

      <div className="flex flex-wrap items-end gap-2 text-xs">
        <div className="rounded-md border border-border bg-card px-2.5 py-1.5">
          <div className="text-2xs text-muted-foreground">Draft</div>
          <div className="font-semibold tabular-nums">{kpis.draft}</div>
        </div>
        <div className="rounded-md border border-border bg-card px-2.5 py-1.5">
          <div className="text-2xs text-muted-foreground">Planned</div>
          <div className="font-semibold tabular-nums">{kpis.planned}</div>
        </div>
        <div className="rounded-md border border-border bg-card px-2.5 py-1.5">
          <div className="text-2xs text-muted-foreground">Open shop floor</div>
          <div className="font-semibold tabular-nums">{kpis.open}</div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[12rem] flex-1">
          <Search className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search plan, SO, process…"
            className="pl-7"
          />
        </div>
        <NativeSelect
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
          className="w-32"
        >
          <option value="all">All status</option>
          {PLAN_STATUSES.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </NativeSelect>
        <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-32" />
        <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-32" />
      </div>

      {error ? (
        <p className="text-sm text-destructive">{(error as Error).message}</p>
      ) : null}

      {!isLoading && filtered.length === 0 ? (
        <EmptyState
          title="No process plans"
          description="Plan HR Parting, CRM, Annealing, Skin Pass, Slitting or Tube against stock and an optional SO line."
          action={
            <Button size="sm" onClick={() => navigate({ to: "/planning/new" })}>
              <Plus />
              New plan
            </Button>
          }
        />
      ) : (
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Plan</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>SO / Client</TableHead>
              <TableHead>Process route</TableHead>
              <TableHead className="text-right">Stages</TableHead>
              <TableHead>Status</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7} className="py-10 text-center text-muted-foreground">
                  Loading…
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>
                    <Link
                      to="/planning/$planId"
                      params={{ planId: row.id }}
                      className="font-mono text-xs font-medium text-primary hover:underline"
                    >
                      {row.plan_no}
                    </Link>
                  </TableCell>
                  <TableCell className="whitespace-nowrap tabular-nums">
                    {format(parseISO(row.plan_date), "dd MMM yyyy")}
                  </TableCell>
                  <TableCell>
                    <div className="text-xs font-medium">{row.so_number || "—"}</div>
                    <div className="text-2xs text-muted-foreground">{row.client_name || ""}</div>
                  </TableCell>
                  <TableCell className="max-w-[16rem] truncate text-xs text-muted-foreground">
                    {row.process_names || "—"}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{row.stage_count ?? 0}</TableCell>
                  <TableCell>
                    <StatusDot tone={statusTone(row.status)}>
                      {labelFor(PLAN_STATUSES, row.status)}
                    </StatusDot>
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button type="button" variant="ghost" size="icon-sm">
                          <MoreHorizontal />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem asChild>
                          <Link to="/planning/$planId" params={{ planId: row.id }}>
                            Open
                          </Link>
                        </DropdownMenuItem>
                        {row.status === "draft" || row.status === "planned" ? (
                          <DropdownMenuItem asChild>
                            <Link to="/planning/$planId/edit" params={{ planId: row.id }}>
                              Edit
                            </Link>
                          </DropdownMenuItem>
                        ) : null}
                        {row.status === "draft" ? (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-destructive"
                              onClick={() => setDeleting(row)}
                            >
                              <Trash2 />
                              Delete
                            </DropdownMenuItem>
                          </>
                        ) : null}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      )}

      <ConfirmDialog
        open={Boolean(deleting)}
        onOpenChange={(o) => {
          if (!o) setDeleting(null);
        }}
        title="Delete draft plan?"
        description={deleting ? `${deleting.plan_no} will be permanently removed.` : undefined}
        confirmLabel="Delete"
        destructive
        loading={remove.isPending}
        onConfirm={async () => {
          if (!deleting) return;
          await remove.mutateAsync(deleting.id);
          setDeleting(null);
        }}
      />
    </div>
  );
}
